import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, auth } from '@archicore/shared';
import { ShieldAlert, LogOut, LayoutGrid, Terminal } from 'lucide-react';

export const RequireSuperAdmin: React.FC<{ children: JSX.Element }> = ({ children }) => {
    const { user, initializing } = useAuth();
    const [isChecking, setIsChecking] = useState(true);

    // Emails autorizados para Acceso Super Admin
    const ADMIN_EMAILS = ['contacto@archicoreanalytic.com', 'arqbonnel@gmail.com', 'master@archicore.app'];

    useEffect(() => {
        if (!initializing) {
            setIsChecking(false);
            if (user) {
                console.log("[RequireSuperAdmin] Usuario detectado:", user.email);
            } else {
                console.log("[RequireSuperAdmin] No hay usuario autenticado.");
            }
        }
    }, [user, initializing]);

    if (initializing || isChecking) {
        return (
            <div className="flex items-center justify-center h-screen bg-[#050505] text-cyan-500">
                <div className="flex flex-col items-center gap-6">
                    <div className="relative">
                        <div className="animate-spin h-14 w-14 border-2 border-cyan-500/20 border-t-cyan-500 rounded-full"></div>
                        <Terminal size={20} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse text-cyan-500" />
                    </div>
                    <div className="space-y-1 text-center">
                        <p className="font-mono text-xs tracking-[0.3em] font-bold text-cyan-500/80 uppercase animate-pulse">Verificando Credenciales</p>
                        <p className="font-mono text-[10px] text-gray-600 uppercase">Seguridad Nivel 5 Activada</p>
                    </div>
                </div>
            </div>
        );
    }

    // Caso 1: No hay usuario -> Redirigir a Login
    if (!user) {
        console.warn("[RequireSuperAdmin] Redirigiendo a /login (Sesión vacía)");
        return <Navigate to="/login" replace />;
    }

    // Caso 2: Hay usuario pero NO está en la lista blanca de administradores
    const normalizedUserEmail = (user.email || '').trim().toLowerCase();
    const isAuthorized = ADMIN_EMAILS.some(email => email.trim().toLowerCase() === normalizedUserEmail);

    if (!isAuthorized) {
        console.error("[RequireSuperAdmin] Acceso DENEGADO para:", normalizedUserEmail);
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#050505] p-6">
                <div className="absolute top-0 left-0 w-full h-1 px-10">
                    <div className="h-full bg-red-500/20 shadow-[0_0_20px_rgba(239,68,68,0.3)]" />
                </div>
                
                <div className="max-w-md w-full glass-card-accent p-10 rounded-3xl text-center border-red-500/20 relative overflow-hidden">
                    {/* Decorative Warning Background */}
                    <div className="absolute -right-20 -top-20 w-40 h-40 bg-red-500/10 blur-[60px] rounded-full" />
                    
                    <div className="w-20 h-20 bg-red-500/10 rounded-2xl flex items-center justify-center border border-red-500/20 mx-auto mb-6">
                        <ShieldAlert size={40} className="text-red-500" />
                    </div>
                    
                    <h1 className="text-2xl font-bold text-white mb-2 italic tracking-tight">Acceso Restringido</h1>
                    <div className="px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full inline-block mb-6">
                        <span className="text-[10px] uppercase font-bold text-red-500 tracking-widest">Protocolo de Incumplimiento</span>
                    </div>

                    <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                        El sistema ha detectado un intento de acceso no autorizado desde la identidad: <br/>
                        <span className="text-white font-mono block mt-2 p-2 bg-gray-900 rounded-lg border border-gray-800 break-all">{user.email}</span>
                    </p>

                    <div className="space-y-3">
                        <button
                            onClick={() => auth.signOut()}
                            className="w-full btn-primary bg-gradient-to-r from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 h-12 flex items-center justify-center gap-2"
                        >
                            <LogOut size={18} />
                            <span>Cerrar Sesión</span>
                        </button>
                        <button
                            onClick={() => window.location.href = '/'}
                            className="w-full btn-secondary h-12 flex items-center justify-center gap-2"
                        >
                            <LayoutGrid size={18} />
                            <span>Volver al Workspace</span>
                        </button>
                    </div>
                    
                    <p className="mt-8 text-[10px] text-gray-600 uppercase tracking-widest font-bold">Registro de evento enviado a ArchiCore HQ</p>
                </div>
            </div>
        );
    }

    // Caso 3: Es Super Admin -> Pase usted, señor.
    console.log("[RequireSuperAdmin] Acceso CONCEDIDO para:", normalizedUserEmail);
    return children;
};
